// Live template doc: the Marqo design-doc template, empty at first; each
// resolved decision is routed by the model into the 1-3 sections it actually
// affects and merged with what's already there.

import { complete } from "../api/model";
import { PRI } from "../api/scheduler";
import { parseModelJSON, salvageSectionUpdates } from "../lib/json-repair";
import { tidyProse } from "../lib/text";
import { docRoutePrompt } from "./prompts";
import { groundingLine, state, type DecisionNode } from "./state";
import { traceParseOutcome } from "./telemetry";

export const DOC_SECTIONS = [
  "Executive Summary", "Problem Statement", "Glossary", "Tenets",
  "Functional Requirements", "Non-Functional Requirements", "Out Of Scope",
  "Success Criteria", "API Design", "Architecture", "Data Storage / Modeling",
  "Low Level Design", "Dependencies", "Consistency and Integrity",
  "Reliability & Resilience", "Scalability", "Observability", "Security",
  "Testing", "Key Risks", "Future Improvements", "Cost Analysis",
  "Release / Roll-out", "Impact on other components",
  "Alternative Solutions Considered", "FAQs", "References", "Appendix",
] as const;

// One-line guidance per section, distilled from the Marqo template's own
// instructions — keeps routed/completed content on-topic for its section.
export const SECTION_GUIDE: Record<string, string> = {
  "Executive Summary": "what this doc is, for whom, the essence of the solution",
  "Problem Statement": "the problem and its context, no solutioning",
  "Glossary": "terms/abbreviations a newcomer would need",
  "Tenets": "ranked guiding principles for tradeoffs",
  "Functional Requirements": "FR-n ids; what users/systems must be able to do",
  "Non-Functional Requirements": "NFR-n ids; latency/availability/scale targets with numbers",
  "Out Of Scope": "what is deliberately excluded and why",
  "Success Criteria": "how production success is measured, tied to FR/NFR ids",
  "API Design": "endpoints/payloads/methods with examples",
  "Architecture": "components and how a request/data flows through them",
  "Data Storage / Modeling": "data model, storage choice, growth over 3-5 years",
  "Low Level Design": "per-component detail: data flow, control flow",
  "Dependencies": "internal/external systems relied on + assumptions about them",
  "Consistency and Integrity": "race conditions and consistency concerns, step-by-step scenarios",
  "Reliability & Resilience": "failure modes and how each is handled",
  "Scalability": "bottleneck points and at what load they appear",
  "Observability": "key metrics, dashboards, alerts",
  "Security": "data sensitivity, least-privilege enforcement",
  "Testing": "unit/integration/stress approach and specific test cases",
  "Key Risks": "what could sink the project: dependencies, resources, unknowns",
  "Future Improvements": "later enhancements and rough timeline",
  "Cost Analysis": "infra cost estimate, growth projection",
  "Release / Roll-out": "phased rollout, backward compatibility, rollback strategy",
  "Impact on other components": "what other teams/components must change",
  "Alternative Solutions Considered": "rejected options and why the choice wins",
  "FAQs": "likely reviewer questions with answers",
  "References": "prior art and related docs",
  "Appendix": "deep supporting analysis",
};

export interface SectionUpdate { section: string; content: string }
export interface CurrentSection { section: string; text: string }

/**
 * The model occasionally wraps an update in a degenerate key
 * ({"u00":{"section":…}}) — unwrap one level so the update isn't lost.
 */
export function normalizeUpdates(updates: any[]): any[] {
  return updates.flatMap((u: any) => {
    if (u && typeof u === "object" && u.section) return [u];
    if (u && typeof u === "object") {
      return Object.values(u).filter((v: any) => v && typeof v === "object" && v.section);
    }
    return [];
  });
}

export async function routeDecisionIntoDoc(node: DecisionNode, currentSections: CurrentSection[]): Promise<SectionUpdate[]> {
  const chosen = node.options[node.chosenIndex!]!;
  const current = currentSections.length
    ? currentSections.map((c) => `### ${c.section}\n${c.text}`).join("\n")
    : "(every section is still empty)";
  const prompt = docRoutePrompt(
    { idea: state.idea, grounding: groundingLine("n/a"), facts: state.facts, constraints: state.constraints },
    node, chosen, current, DOC_SECTIONS, SECTION_GUIDE,
  );
  const raw = await complete(prompt, { max: 620, temp: 0.4, priority: PRI.GROUND, kind: "doc.route" });
  let { data, parse } = parseModelJSON(raw);
  if ((!data || !Array.isArray(data.updates)) && raw.includes('"section"')) {
    // envelope corrupt — pull whatever section/content pairs survive
    const pairs = salvageSectionUpdates(raw);
    if (pairs.length) { data = { updates: pairs }; parse = "repaired"; }
  }
  traceParseOutcome("doc.route", parse, raw);
  if (!data || !Array.isArray(data.updates)) return [];
  const byName = new Map<string, string>(DOC_SECTIONS.map((s) => [s.toLowerCase(), s]));
  return normalizeUpdates(data.updates).slice(0, 3).map((u: any) => ({
    section: byName.get(String(u.section || "").trim().toLowerCase()),
    content: String(u.content || "").split("\n").map((l: string) => tidyProse(l)).filter(Boolean).join("\n"),
  })).filter((u: any): u is SectionUpdate => Boolean(u.section && u.content));
}
